import React from 'react';
import { UI_COLORS } from '../../constants';

// Web port of the warehouse app's BatchGrid. Renders the cart as a grid
// of tote squares (row-major, 1-indexed) and can highlight totes in any
// color - used totes, return totes, unpickable totes, tap-to-filter.
//
// highlightedTotes: [{ row, column, color }] - 1-indexed positions
// onTotePress: called with { row, column, toteId } where toteId is
// 'TOTE-{row}-{column}'; cells become buttons when provided
// pressedTotes: [{ row, column }] - cells announced as pressed (filtering)
// toteCount: totes fill row-major from the top; beyond it cells render
// dashed/dimmed and are not pressable
// ariaLabel: description used when the grid is non-interactive, which
// collapses it to a single role=img node for assistive tech

const formatToteId = (row, column) => `TOTE-${row}-${column}`;

const toteBaseStyle = {
  backgroundColor: 'rgba(7,8,8,0.12)',
  border: `1px solid ${UI_COLORS.border}`,
  boxSizing: 'border-box',
  padding: 0,
};

const toteUnusedStyle = {
  backgroundColor: UI_COLORS.warm,
  border: '1px dashed rgba(7,8,8,0.12)',
  opacity: 0.4,
};

export default function BatchGrid({
  totalRows,
  totalColumns,
  toteSize = 48,
  gap = 8,
  highlightedTotes = [],
  onTotePress,
  pressedTotes = [],
  toteCount,
  ariaLabel,
  style,
}) {
  const highlightMap = new Map(
    highlightedTotes.map(h => [`${h.row}-${h.column}`, h.color])
  );
  const pressedSet = new Set(
    pressedTotes.map(t => `${t.row}-${t.column}`)
  );

  const rows = [];
  for (let row = 1; row <= totalRows; row++) {
    const cells = [];
    for (let column = 1; column <= totalColumns; column++) {
      const color = highlightMap.get(`${row}-${column}`);
      // Totes fill row-major from the top; beyond toteCount are unused
      const inUse = toteCount === undefined || (row - 1) * totalColumns + column <= toteCount;
      const cellStyle = {
        ...toteBaseStyle,
        width: toteSize,
        height: toteSize,
        borderRadius: Math.max(4, toteSize * 0.12),
        ...(color ? { backgroundColor: color, borderColor: color } : null),
        ...(!inUse ? toteUnusedStyle : null),
        ...(onTotePress && inUse ? { cursor: 'pointer' } : null),
      };

      const onPress = () => onTotePress({ row, column, toteId: formatToteId(row, column) });

      cells.push(
        onTotePress ? (
          <button
            type="button"
            key={column}
            style={cellStyle}
            disabled={!inUse}
            onClick={onPress}
            aria-label={formatToteId(row, column)}
            aria-pressed={pressedSet.has(`${row}-${column}`)}
          />
        ) : (
          <div key={column} style={cellStyle} aria-hidden="true" />
        )
      );
    }
    rows.push(
      <div key={row} style={{ display: 'flex', gap }}>
        {cells}
      </div>
    );
  }

  const usedCells = toteCount ?? highlightedTotes.length;
  const summary = ariaLabel ||
    `Cart layout: ${usedCells} of ${totalRows * totalColumns} tote positions filled`;

  return (
    <div
      style={{ display: 'inline-flex', flexDirection: 'column', gap, ...style }}
      {...(onTotePress
        ? { role: 'group', 'aria-label': ariaLabel || 'Tote positions' }
        : { role: 'img', 'aria-label': summary })}
    >
      {rows}
    </div>
  );
}
